import React from 'react';
import styles from './ProductRoadmapPage.module.scss';

const URL_RE = /(https:\/\/[^\s<>"')\]]+(?:\([^\s<>"')\]]*\))*)/g;

function linkifyPlainText(text: string): React.ReactNode {
  const segments = text.split(URL_RE);
  return segments.map((segment, idx) => {
    if (segment.startsWith('https://')) {
      const href = segment.replace(/[.,;:!?]+$/, '');
      return (
        <a key={idx} href={href} target="_blank" rel="noopener noreferrer">
          {href}
        </a>
      );
    }
    return segment;
  });
}

/** Markdown-style `**phrase**` → `<strong class="…roadmapEmphasis">` (same class as bullet labels). */
function formatInline(text: string): React.ReactNode {
  const BOLD = /\*\*(.+?)\*\*/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let partKey = 0;
  while ((match = BOLD.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        <React.Fragment key={`t-${partKey++}`}>
          {linkifyPlainText(text.slice(lastIndex, match.index))}
        </React.Fragment>,
      );
    }
    parts.push(
      <strong className={styles.roadmapEmphasis} key={`b-${partKey++}`}>
        {match[1]}
      </strong>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(
      <React.Fragment key={`t-${partKey++}`}>
        {linkifyPlainText(text.slice(lastIndex))}
      </React.Fragment>,
    );
  }
  if (parts.length === 0) {
    return linkifyPlainText(text);
  }
  return <>{parts}</>;
}

/** Bolds the leading label before the first ASCII or fullwidth colon (e.g. "Faster event creation:"). */
function formatBulletLine(item: string): React.ReactNode {
  const colonMatch = item.match(/[:：]/);
  const colon = colonMatch?.index ?? -1;
  const delimiter = colonMatch?.[0] ?? ':';
  if (colon === -1) {
    return linkifyPlainText(item);
  }
  const label = item.slice(0, colon);
  const rest = item.slice(colon + delimiter.length);
  return (
    <>
      <strong className={styles.roadmapEmphasis}>
        {label}
        {delimiter}
      </strong>
      {formatInline(rest)}
    </>
  );
}

function renderBlocks(text: string): React.ReactNode {
  const blocks = text
    .split(/\n\n+/)
    .map((b) => b.trim())
    .filter(Boolean);

  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n').map((l) => l.trimEnd());
        const bulletStart = lines.findIndex((l) => l.trimStart().startsWith('- '));

        if (bulletStart === -1) {
          return <p key={i}>{formatInline(block)}</p>;
        }

        const beforeLines = lines
          .slice(0, bulletStart)
          .map((l) => l.trim())
          .filter(Boolean);
        const bulletLines = lines
          .slice(bulletStart)
          .map((l) => l.trim())
          .filter((l) => l.startsWith('- '))
          .map((l) => l.replace(/^- /, '').trim());

        return (
          <React.Fragment key={i}>
            {beforeLines.length > 0 ? <p>{formatInline(beforeLines.join('\n'))}</p> : null}
            <ul>
              {bulletLines.map((item, j) => (
                <li key={j}>{formatBulletLine(item)}</li>
              ))}
            </ul>
          </React.Fragment>
        );
      })}
    </>
  );
}

/**
 * Renders roadmap copy stored as plain text with `\n\n` paragraph breaks and
 * optional Markdown-style bullet lines (`- item`) grouped in blocks.
 * Inline `**phrase**` becomes bold. Plain `https://` substrings become links.
 * Avoids ICU `<` rich-text issues.
 */
export function renderProductRoadmapRichText(text: string): React.ReactNode {
  // Normalize inline bullet separators that lost their newlines (e.g. after
  // Prettier flattens JSON strings): "…spot. - Save before…" → "…spot.\n- Save…"
  const normalized = text.trim().replace(/([.!?]) - /g, '$1\n- ');
  return renderBlocks(normalized);
}
